import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

const BulkContext = createContext();

export const BulkProvider = ({
  children,
  getItem,
  importBatchId,
  module,
  resetItem,
  placeholder = true,
  ...props
}) => {
  const { importBatch } = useSelector(
    ({ ingest: { importBatch } }) => ({ importBatch }),
    shallowEqual,
  );

  const dispatch = useDispatch();

  const { isError, isLoading, isSuccess } = getItem(importBatchId, {
    refetchOnMountOrArgChange: true,
    skip: !importBatchId || !module,
  });

  // Decide whether to show children or loader
  const shouldShowChildren = isSuccess && importBatch && (!placeholder || !isLoading);

  useEffect(() => {
    return () => {
      if (resetItem) {
        dispatch(resetItem());
      }
    };
  }, [dispatch, resetItem]);

  if (isError) return <Navigate to="/" />;

  return (
    <BulkProviderNested importBatchId={importBatchId} module={module} {...props}>
      {shouldShowChildren ? children : null}
    </BulkProviderNested>
  );
};

export const BulkProviderNested = ({
  children,
  fieldErrorDetail,
  importBatchId,
  module,
  setFieldErrorDetail,
  initialStep = 0,
  steps = [],
}) => {
  const [activeStep, setActiveStep] = useState(initialStep);

  const maxIndex = steps.length ? steps.length - 1 : 0;

  const toPreviousStep = useCallback(() => setActiveStep(s => Math.max(0, s - 1)), []);

  const toNextStep = useCallback(() => setActiveStep(s => Math.min(maxIndex, s + 1)), [maxIndex]);

  const goToStep = useCallback(
    idx => {
      const n = Number(idx) || 0;
      setActiveStep(Math.min(Math.max(0, n), maxIndex + 1));
    },
    [maxIndex],
  );

  return (
    <BulkContext.Provider
      value={{
        activeStep,
        fieldErrorDetail,
        goToStep,
        importBatchId,
        module,
        setFieldErrorDetail,
        steps,
        toNextStep,
        toPreviousStep,
      }}
    >
      {children}
    </BulkContext.Provider>
  );
};

export const useBulkContext = () => {
  const ctx = useContext(BulkContext);
  if (!ctx) {
    throw new Error("useBulkContext must be used inside <BulkProvider>");
  }
  return ctx;
};
